import {
  Check,
  Package,
  Database,
  LayoutGrid,
  Layers,
  Loader2,
  Download,
  Upload,
} from "lucide-react";
import { useContext, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import MerchantContext from "../../contexts/MerchantContext";
import UserContext from "../../contexts/UserContext";
import { trpc } from "../../trpc";
import InventoryCard from "./InventoryCard";
import InventoryConfigModal from "./InventoryConfigModal";

type InventorySystemType = "simi" | "mobilia" | "domus" | "softim" | "smarthome" | "wasi";

type MercadolibreRow = {
  mercadolibreCode?: unknown;
  operationType?: unknown;
};

// Mirrors what the enrichment index builder expects: a list of publications, each
// carrying MercadoLibre's own id and which side (rent/sale) it is published under.
// Rows without a `wasiCode` are allowed — they simply match nothing.
const validateMercadolibreJson = async (file: File): Promise<string | null> => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(await file.text());
  } catch {
    return "El archivo no es un JSON válido.";
  }

  if (!Array.isArray(parsed)) return "El archivo debe contener una lista de publicaciones.";
  if (parsed.length === 0) return "El archivo está vacío.";

  const isInvalid = (row: MercadolibreRow | null) =>
    !row ||
    typeof row.mercadolibreCode !== "string" ||
    !/^MCO\d+$/.test(row.mercadolibreCode) ||
    (row.operationType !== "rent" && row.operationType !== "sale");

  if ((parsed as (MercadolibreRow | null)[]).some(isInvalid)) {
    return 'Formato inválido. Cada publicación necesita "mercadolibreCode" (ej. MCO4321066898) y "operationType" ("rent" o "sale").';
  }

  return null;
};

type InventoryConfigurationProps = {
  merchant: any;
};

const InventoryConfiguration = ({ merchant }: InventoryConfigurationProps): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState<{
    type: InventorySystemType;
    name: string;
    config?: any;
  } | null>(null);

  const { data: inventoryStatus, refetch } = trpc.inventory.status.useQuery(
    { merchantId },
    {
      enabled: !!merchantId,
    }
  );

  const hasListings = useMemo(() => !!inventoryStatus?.hasListings, [inventoryStatus]);
  const canSync = useMemo(() => !!inventoryStatus?.canSync, [inventoryStatus]);
  const relaxedSearchEnabled = useMemo(() => merchant?.relaxedSearchEnabled ?? false, [merchant]);

  const inventorySystems = useMemo(() => {
    const merchantData = merchant as any;
    const allSystems = [
      {
        id: "simi" as InventorySystemType,
        name: "Simi - Inventario",
        description: "Gestión completa de inventario de propiedades",
        isConnected: !!merchantData?.simi?.isConfigured,
        config: merchantData?.simi,
        icon: <Package size={28} color="#4A5568" />,
      },
      {
        id: "mobilia" as InventorySystemType,
        name: "Mobilia - Inventario",
        description: "Sistema de inventario inmobiliario avanzado",
        isConnected: !!merchantData?.mobilia?.isConfigured,
        config: merchantData?.mobilia,
        icon: <Database size={28} color="#4A5568" />,
      },
      {
        id: "softim" as InventorySystemType,
        name: "Softim",
        description: "Software de gestión inmobiliaria",
        isConnected: !!merchantData?.softim?.isConfigured,
        config: merchantData?.softim,
        icon: <LayoutGrid size={28} color="#4A5568" />,
      },
      {
        id: "domus" as InventorySystemType,
        name: "Domus",
        description: "Sistema de gestión de propiedades",
        isConnected: !!merchantData?.domus?.isConfigured,
        config: merchantData?.domus,
        icon: <Layers size={28} color="#4A5568" />,
      },
      {
        id: "smarthome" as InventorySystemType,
        name: "SmartHome",
        description: "Gestión de desarrollos inmobiliarios",
        isConnected: !!merchantData?.smarthome?.isConfigured,
        config: merchantData?.smarthome,
        icon: <LayoutGrid size={28} color="#4A5568" />,
      },
      {
        id: "wasi" as InventorySystemType,
        name: "WASI",
        description: "Conecta tu cuenta de WASI para sincronizar tu inventario",
        isConnected: !!merchantData?.wasi?.isConfigured,
        config: merchantData?.wasi,
        icon: <Database size={28} color="#4A5568" />,
      },
    ];

    const connectedSystem = allSystems.find((system) => system.isConnected);
    return connectedSystem ? [connectedSystem] : allSystems;
  }, [merchant]);

  const handleConfigureSystem = (
    systemId: InventorySystemType,
    systemName: string,
    config?: any
  ) => {
    setSelectedSystem({ type: systemId, name: systemName, config });
    setIsOpen(true);
  };

  const utils = trpc.useUtils();

  const manualSync = trpc.inventory.manualSync.useMutation({
    onSuccess: async (data) => {
      if (!data?.started) {
        toast.warning("No se pudo iniciar la sincronización", {
          description: "La sincronización no se pudo iniciar. Verifica tu configuración de CRM.",
        });
        return;
      }

      await utils.inventory.status.invalidate({ merchantId });
      toast.success("Sincronización iniciada", {
        description:
          "Se ha iniciado la sincronización de su inventario. En pocos minutos sus inmuebles estarán sincronizados con el agente.",
      });
    },
    onError: (error) => {
      toast.error("Error al sincronizar", {
        description: error.message || "Ocurrió un error al intentar sincronizar el inventario.",
      });
    },
  });

  const removeInventoryConfig = trpc.merchants.removeInventoryConfig.useMutation({
    onSuccess: async () => {
      await utils.merchants.get.invalidate({ id: merchantId });
      await utils.inventory.status.invalidate({ merchantId });
      toast.success("Configuración eliminada", {
        description: "La configuración del inventario ha sido eliminada exitosamente.",
      });
    },
    onError: (error) => {
      toast.error("Error al eliminar configuración", {
        description: error.message || "Ocurrió un error al intentar eliminar la configuración.",
      });
    },
  });

  const handleManualSync = async () => {
    if (!merchantId) return;
    await manualSync.mutateAsync({ merchantId, type: "full" });
    await refetch();
  };

  const mercadolibreFileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingMercadolibre, setIsUploadingMercadolibre] = useState(false);

  // MercadoLibre blocks automated listing enumeration, so the file is produced
  // out-of-band and uploaded here; the inventory sync reads it from S3. Palomma-only:
  // the same check gates the procedures themselves (helpers/permissions.ts), so this
  // only decides whether the card renders — it is not the security boundary.
  const { hasPalommaOnlyAccess } = useContext(UserContext);
  const canUploadMercadolibre = hasPalommaOnlyAccess && Boolean(merchant?.wasi);
  const mercadolibreFileStatus = trpc.inventory.mercadolibreFileStatus.useQuery(undefined, {
    enabled: canUploadMercadolibre,
  });
  const getMercadolibreUploadUrl = trpc.inventory.getMercadolibreUploadUrl.useMutation();

  const handleMercadolibreFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    // Reset so selecting the same file again re-triggers onChange
    event.target.value = "";
    if (!file) return;

    const validationError = await validateMercadolibreJson(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsUploadingMercadolibre(true);
    try {
      const { uploadUrl } = await getMercadolibreUploadUrl.mutateAsync();

      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": "application/json" },
      });

      if (!uploadResponse.ok) throw new Error("Failed to upload file to S3");

      toast.success("Archivo de Mercado Libre subido correctamente.");
      await utils.inventory.mercadolibreFileStatus.invalidate();
    } catch (error) {
      console.error("Error uploading Mercado Libre file:", error);
      toast.error("No se pudo subir el archivo. Inténtalo de nuevo.");
    } finally {
      setIsUploadingMercadolibre(false);
    }
  };

  const scrapePortals = trpc.inventory.scrapePortals.useMutation({
    onSuccess: () => {
      toast.success("Enriquecimiento de inventario iniciado");
    },
    onError: (error) => {
      toast.error("Error al iniciar el enriquecimiento", {
        description:
          error.message || "Ocurrió un error al iniciar el enriquecimiento de inventario.",
      });
    },
  });

  const handleDisconnect = async (crmType: InventorySystemType) => {
    if (!merchantId) return;
    await removeInventoryConfig.mutateAsync({ merchantId, crmType });
  };

  const updateRelaxedSearch = trpc.inventory.updateRelaxedSearch.useMutation({
    onSuccess: async (data) => {
      await utils.merchants.get.invalidate({ id: merchantId });
      toast.success("Configuración actualizada", {
        description: `Búsqueda relajada ${data.relaxedSearchEnabled ? "activada" : "desactivada"} exitosamente.`,
      });
    },
    onError: (error) => {
      toast.error("Error al actualizar", {
        description: error.message || "Ocurrió un error al actualizar la configuración.",
      });
    },
  });

  const handleRelaxedSearchToggle = async (checked: boolean) => {
    if (!merchantId) return;
    await updateRelaxedSearch.mutateAsync({
      merchantId,
      relaxedSearchEnabled: checked,
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="mb-2 text-2xl font-bold">Sistemas de Inventario</h2>
        <p className="mb-6 text-gray-600">
          Conecta tu sistema de gestión inmobiliaria para sincronizar tu inventario
        </p>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {inventorySystems.map((system) => (
            <InventoryCard
              key={system.id}
              name={system.name}
              description={system.description}
              isConnected={system.isConnected}
              hasListings={hasListings && system.isConnected}
              icon={system.icon}
              onConfigure={() => handleConfigureSystem(system.id, system.name, system.config)}
              onDisconnect={system.isConnected ? () => handleDisconnect(system.id) : undefined}
              isDisconnecting={removeInventoryConfig.isLoading}
            />
          ))}
        </div>
      </div>

      {selectedSystem && (
        <InventoryConfigModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          systemType={selectedSystem.type}
          systemName={selectedSystem.name}
          existingConfig={selectedSystem.config}
        />
      )}

      {merchant?.crm && canSync && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex flex-col items-start gap-4">
            {hasListings ? (
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <Check color="#38A169" size={20} />
                  <p className="font-semibold text-green-600">Inventario sincronizado</p>
                </div>
                <p className="text-sm text-gray-600">
                  Tu inventario está actualmente sincronizado. Puedes forzar una nueva
                  sincronización si necesitas actualizar los datos.
                </p>
              </div>
            ) : (
              <p className="text-gray-700">
                No encontramos inmuebles sincronizados todavía. Inicia la sincronización para
                comenzar.
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                variant={hasListings ? "outline" : "main"}
                onClick={handleManualSync}
                disabled={manualSync.isLoading}
              >
                {manualSync.isLoading && <Loader2 className="size-4 animate-spin" />}
                {hasListings ? "Sincronizar de nuevo" : "Sincronizar inventario"}
              </Button>
              {(merchant?.wasi?.fincaraizProfileUrl ||
                merchant?.wasi?.metrocuadradoProfileUrl) && (
                <Button
                  variant="outline"
                  onClick={() => scrapePortals.mutate({ merchantId })}
                  disabled={scrapePortals.isLoading}
                >
                  {scrapePortals.isLoading && <Loader2 className="size-4 animate-spin" />}
                  Enriquecimiento Inventario
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {canUploadMercadolibre && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex flex-col items-start gap-4">
            <div>
              <p className="mb-1 font-semibold">Códigos de Mercado Libre</p>
              <p className="text-sm text-gray-600">
                Sube el archivo JSON con las publicaciones de Mercado Libre. La
                sincronización de inventario lo usa para asignar el código de Mercado
                Libre a cada inmueble. Al subir un archivo nuevo se reemplaza el actual.
              </p>
            </div>

            {mercadolibreFileStatus.isLoading ? (
              <Loader2 className="size-4 animate-spin text-gray-500" />
            ) : mercadolibreFileStatus.data?.exists ? (
              <div className="flex flex-col gap-1">
                <p className="text-sm text-gray-600">
                  Última actualización:{" "}
                  <span className="font-medium text-gray-800">
                    {mercadolibreFileStatus.data.lastModified
                      ? dayjs(mercadolibreFileStatus.data.lastModified).format(
                          "DD/MM/YYYY h:mm A"
                        )
                      : "—"}
                  </span>
                </p>
                {mercadolibreFileStatus.data.downloadUrl && (
                  <a
                    href={mercadolibreFileStatus.data.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium text-palomma-primary hover:underline"
                  >
                    <Download className="size-4" />
                    Descargar archivo actual
                  </a>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-600">Aún no se ha subido ningún archivo.</p>
            )}

            <input
              ref={mercadolibreFileInputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={handleMercadolibreFileSelected}
            />
            <Button
              variant="outline"
              disabled={isUploadingMercadolibre}
              onClick={() => mercadolibreFileInputRef.current?.click()}
            >
              {isUploadingMercadolibre ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Upload className="size-4" />
              )}
              {isUploadingMercadolibre ? "Subiendo..." : "Subir archivo"}
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <Label htmlFor="relaxed-search" className="mb-1 font-semibold">
              Búsqueda Flexible
            </Label>
            <p className="mt-0 text-sm text-gray-600">
              Permite búsquedas más flexibles que incluyen propiedades con características similares
              a las solicitadas.
            </p>
          </div>
          <button
            type="button"
            id="relaxed-search"
            role="switch"
            aria-checked={relaxedSearchEnabled}
            onClick={() => handleRelaxedSearchToggle(!relaxedSearchEnabled)}
            disabled={updateRelaxedSearch.isLoading}
            className={cn(
              "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50",
              relaxedSearchEnabled ? "bg-palomma-primary" : "bg-gray-300"
            )}
          >
            <span
              className={cn(
                "pointer-events-none block size-4 rounded-full bg-white shadow-sm transition-transform",
                relaxedSearchEnabled ? "translate-x-4" : "translate-x-0"
              )}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InventoryConfiguration;
